import React, { useRef, useState, useEffect } from "react";
import ReactQuill from "react-quill";
import mixins from "../../../app-services/mixins";
import "react-quill/dist/quill.snow.css";

/**
 * Dedication component — updated to disallow image uploads/paste/drop.
 */
function Dedication(props) {
  const {
    alert,
    editmode,
    editstep = 0,
    data,
    onProceed = () => {},
    closable = false,
    onClose = () => ({}),
    setCustomCharData = () => {},
    defaultdedication,
  } = props;

  const customization =
    props?.store?.customCharacter || props?.customCharacter || [];
  const [formdata, setFormdata] = useState({ dedication: "" });
  const cardref = useRef();
  const [overlayStyle, setOverlayStyle] = useState({});
  const [dedicationError, setDedicationError] = useState(false);
  const [dedicationCount, setDedicationCount] = useState(0);
  const quillRef = useRef();
  const dedicationMaxLength = 125;
  const allowedDedicationCharacter = /^[a-zA-Z0-9 \n\r\t.,!?'"():;]$/;

  const sanitizeDedicationText = (text = "") =>
    Array.from(String(text).replace(/\u00a0/g, " "))
      .filter((character) => allowedDedicationCharacter.test(character))
      .join("");

  const cleanHeadingsToP = (content) => {
    if (!content) return "";
    for (let i = 1; i <= 6; i++) {
      content = content.replace(new RegExp(`<h${i}[^>]*>`, "g"), "<p>");
      content = content.replace(new RegExp(`</h${i}>`, "g"), "</p>");
    }
    content = content.replace(/\s*(id|class)="[^"]*"/g, "");
    content = content.replace(/<p>\s*<\/p>/g, "");
    content = content.replace(/\n\s*\n/g, "\n");
    return content.trim();
  };

  const stripHtmlAndCountText = (html) => {
    if (!html) return 0;
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;
    const textNodes = Array.from(tempDiv.childNodes)
      .filter(
        (node) =>
          node.nodeType === Node.TEXT_NODE ||
          (node.nodeType === Node.ELEMENT_NODE && node.tagName !== "BR")
      )
      .map((node) => node.textContent.trim())
      .filter((text) => text.length > 0);
    return textNodes.join(" ").length;
  };
  const sanitizeDedicationHtml = (html) => {
    if (!html) return "";
    html = html.replace(/<img[^>]*>/gi, "");
    html = html.replace(/<figure[^>]*>[\s\S]*?<\/figure>/gi, "");
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    const textNodes = document.createTreeWalker(tmp, NodeFilter.SHOW_TEXT);
    let currentNode = textNodes.nextNode();

    while (currentNode) {
      currentNode.nodeValue = sanitizeDedicationText(currentNode.nodeValue);
      currentNode = textNodes.nextNode();
    }

    let text = tmp.textContent || tmp.innerText || "";
    text = text.trim();

    // If the whole text is just the artifact "le", drop it
    if (text.toLowerCase() === "le") return "";

    // If text starts with "le " (artifact), remove that leading artifact.
    if (text.toLowerCase().startsWith("le ")) {
      text = text.replace(/^le\s+/i, "");
    }

    // If incoming html has markup besides images, keep cleaned markup, otherwise wrap plain text in <p>
    const hasTag = /<\/?[a-z][\s\S]*>/i.test(html);
    if (hasTag) {
      // Clean headings and return
      return cleanHeadingsToP(tmp.innerHTML);
    } else {
      return `<p>${text}</p>`;
    }
  };

  // Prevent pasted/dropped files (images) — returns true if prevented
  const preventImageFilesFromEvent = (e) => {
    const clipboard = e.clipboardData;
    const dt = e.dataTransfer;
    const files =
      (clipboard && clipboard.files && clipboard.files.length) ||
      (dt && dt.files && dt.files.length) ||
      0;
    if (files > 0) {
      // check if any file is an image
      const items = (clipboard && clipboard.items) || (dt && dt.items) || null;
      if (items) {
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          const type = item.type || (item.kind === "file" && item.type);
          if (type && type.startsWith && type.startsWith("image/")) {
            e.preventDefault();
            return true;
          }
        }
      } else {
        // fallback: if files exist, just block
        e.preventDefault();
        return true;
      }
    }
    return false;
  };

  useEffect(() => {
    const dedStep =
      customization &&
      customization.find &&
      customization.find((s) => s.name === "dedication");
    const dedValue =
      dedStep &&
      dedStep.ans &&
      (dedStep.ans.dedication || dedStep.ans?.value || dedStep.ans?.text)
        ? dedStep.ans.dedication || dedStep.ans.value || dedStep.ans.text
        : "";

    let parsed = dedValue;
    if (typeof parsed === "string") {
      try {
        const maybe = JSON.parse(parsed);
        if (typeof maybe === "string") parsed = maybe;
      } catch (e) {
        // ignore parse errors
      }
    }
    const initialHtml =
      sanitizeDedicationHtml(parsed) ||
      sanitizeDedicationHtml(defaultdedication);
    setFormdata({ dedication: initialHtml });
    setDedicationCount(stripHtmlAndCountText(initialHtml));
    setDedicationError(stripHtmlAndCountText(initialHtml) > dedicationMaxLength);
  }, [customization, defaultdedication]);

  const goBack = () => {
    onClose();
  };

  const persistDedicationToStore = (dedicationHtml) => {
    try {
      const tmp = Array.isArray(customization)
        ? JSON.parse(JSON.stringify(customization))
        : [];
      const idx = tmp.findIndex((s) => s.name === "dedication");
      const newAns = { dedication: dedicationHtml };

      if (idx !== -1) {
        tmp[idx].ans = { ...(tmp[idx].ans || {}), ...newAns };
      } else {
        tmp.push({
          question: "Enter your dedication",
          type: "image-form",
          name: "dedication",
          formStep: [
            { value: 1, label: "Name" },
            { value: 2, label: "Gender" },
            { value: 3, label: "Skin Tone" },
            { value: 4, label: "T-shirt Colour" },
            { value: 5, label: "Hair Style" },
            { value: 6, label: "Hair Colour" },
            { value: 7, label: "Eyewear" },
          ],
          hidden: true,
          ans: newAns,
        });
      }
      mixins.setStore(
        "customCharacter",
        tmp.map((temp) =>
          Object.fromEntries(
            Object.entries(temp).filter(
              ([k]) => !["collect", "overlay"].includes(k)
            )
          )
        ),
        props
      );
    } catch (err) {
      console.error("Failed to persist dedication to store:", err);
    }
  };

  const handleChange = (val) => {
    setFormdata(val);
  };

  const isEmptyHtml = (html = "") => {
    return (
      html
        .replace(/<[^>]*>/g, "") // remove HTML tags
        .replace(/&nbsp;/g, "")
        .trim() === ""
    );
  };

  const handlesubmit = (e) => {
    e.preventDefault();
    const dedicationHtml =
      !formdata?.dedication || isEmptyHtml(formdata.dedication)
        ? defaultdedication
        : formdata.dedication;
    persistDedicationToStore(dedicationHtml);
    setTimeout(() => {
      onProceed();
    }, 500);
  };

  const quillModules = {
    toolbar: false,
    clipboard: {
      matchVisual: false,
    },
  };

  const handleDedicationPaste = (e) => {
    if (preventImageFilesFromEvent(e)) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    const clipboardText =
      e.clipboardData?.getData("text/plain") ||
      e.nativeEvent?.clipboardData?.getData("text/plain") ||
      "";
    const cleanText = sanitizeDedicationText(clipboardText);

    if (!cleanText) {
      return;
    }

    const quill = quillRef.current?.getEditor?.();
    if (!quill) {
      return;
    }

    const range = quill.getSelection(true) || {
      index: quill.getLength(),
      length: 0,
    };
    const selectedTextLength = range?.length
      ? quill.getText(range.index, range.length).trim().length
      : 0;
    const currentCount = stripHtmlAndCountText(formdata?.dedication || "");
    const availableLength = Math.max(
      0,
      dedicationMaxLength - (currentCount - selectedTextLength)
    );
    const textToInsert = cleanText.slice(0, availableLength);

    if (!textToInsert) {
      setDedicationError(true);
      return;
    }

    quill.deleteText(range.index, range.length, "user");
    quill.insertText(range.index, textToInsert, "user");
    quill.setSelection(range.index + textToInsert.length, 0, "silent");
  };

  // formats intentionally exclude 'image'
  const quillFormats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "list",
    "bullet",
    "indent",
    "link",
    "code",
    "code-block",
    "script",
    "align",
  ];

  return (
    <div className="personolize" style={overlayStyle} ref={cardref}>
      <i className="close fa fa-times" onClick={() => onClose()} />
      <div className="row py-5">
        <div className="col-12">
          <h5 className="mb-0">Write a special message here:</h5>
          <p style={{ color: "red", fontSize: "12px" }}>
            We’ll print exactly what you’ve typed, so do double-check! Please
            only use letters, numbers and keyboard characters (no emojis or
            images)
          </p>

          <ReactQuill
            ref={quillRef}
            theme="snow"
            value={formdata?.dedication || ""}
            onBeforeInput={(e) => {
              if (e.data && sanitizeDedicationText(e.data) !== e.data) {
                e.preventDefault();
              }
            }}
            onChange={(content, delta, source, editor) => {
              let updatedContent = cleanHeadingsToP(content);
              let new_content =
                mixins && mixins.preventEmojicontent
                  ? mixins.preventEmojicontent(updatedContent)
                  : updatedContent;
              const safeHtml = sanitizeDedicationHtml(new_content);

              const count = stripHtmlAndCountText(safeHtml);
              if (count <= dedicationMaxLength) {
                setDedicationError(false);
                handleChange({ ...formdata, dedication: safeHtml });
              } else {
                setDedicationError(true);
              }
            }}
            placeholder="A special message"
            modules={quillModules}
            formats={quillFormats}
            onPaste={handleDedicationPaste}
            onDrop={(e) => {
              if (preventImageFilesFromEvent(e)) {
                return;
              }
              e.preventDefault();
            }}
            className={`editor ${dedicationError ? "border-red-500" : ""}`}
          />

          <div
            style={{
              fontSize: "12px",
              marginTop: "4px",
              color: dedicationError ? "red" : "#6b7280",
            }}
          >
            {dedicationError ? "Maximum 125 characters allowed" : ``}
          </div>
        </div>

        <div className="col-12 mt-2">
          <div>
            <button
              id="action-btn"
              className="action-button"
              disabled={dedicationError}
              onClick={handlesubmit}
            >
              Save & Preview
            </button>
            <button
              type="button"
              style={{ marginLeft: 8 }}
              className="action-button secondary"
              onClick={goBack}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default connect(Dedication);
